/**
 * FirmaFuarKurallariPage.jsx — "Fuar Kuralları" (backlog #536 adım 6, #540, #553): Katılımcı portalında onay metinleri.
 *
 * Giriş geçidi (Routes/OnayKapisi.js) bunları girişte sorar; bu sayfa onlara sonradan dönüp okumak içindir ve GERÇEK onay yazar:
 * "Onaylıyorum" POST /{key}/accept çağırır. #553'ten beri sayfa GET /onay-metinleri/current okur (backend #552): her kullanıcı metni,
 * SEÇİLİ FUARDA yürürlükteki sürümüyle (o fuarın kendi sürümü varsa o, yoksa global) ve çağıranın o sürümü onaylayıp onaylamadığıyla
 * (`onaylandi`). Bir fuar kendi sürümünü yayınlayınca satır yeniden onaya döner. Metinlerin gövdesi HTML + PDF / görsel dosyalardır
 * (Components/Common/OnayMetniGovdesi.jsx); fuar notu (`ekNot`) kurallar penceresinin altında okunur ve tek onay düğmesiyle birlikte onaylanır (#668; WAS: ayrı kutucuk).
 *
 * İki satır, her biri bir pencere açar (Components/Common/OnayMetniPencereleri.jsx, geçitle ortak): KVKK Onamı (yalnızca `kvkk`) ve
 * Fuar Kuralları (kalan her metin art arda, tek onayla). Personel (portal önizlemesi) metinleri okur ama onaylayamaz: kendi adına bir
 * kayıt düşmesin.
 *
 * Taahhütnameler (hedef 3) burada YOK: onlar proje başına, proje sayfasında onaylanır (#216).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, CardBody, CardHeader, Container, Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import { KuralSatiri, OnayPenceresi, PENCERELER, pencereGruplari } from "../../Components/Common/OnayMetniPencereleri";
import { usePortal } from "../../Components/Contexts/PortalContext";
import { DIL, getGuncelMetinler, onayKaydet } from "../../services/onayMetniService";
import { isStaffAccount } from "../../services/firmaSessionService";
import i18n from "../../i18n";

export default function FirmaFuarKurallariPage() {
  const { t, i18n: dil } = useTranslation("portal");
  const dilId = dil?.language === "en" ? DIL.EN : DIL.TR;
  const personel = isStaffAccount();
  const fuarId = usePortal()?.fuarId ?? null;
  const [metinler, setMetinler] = useState(null);     // null = ilk okuma sürüyor
  const [hata, setHata] = useState(null);
  const [deneme, setDeneme] = useState(0);
  const [acikPencere, setAcikPencere] = useState(null);

  useEffect(() => { document.title = i18n.t("portal:fuarKurallari.sayfaBasligiFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect). Dil ya da fuar değişince yeniden okunur;
  // eski liste yeni gelene kadar ekranda kalır (açık pencere yeniden kurulmasın).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        const out = await getGuncelMetinler({ languageId: dilId, fuarId });
        if (!ignore) { setMetinler(out); setHata(null); }
      } catch (e) {
        if (!ignore) { setHata(e.message); setMetinler((onceki) => onceki ?? []); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [dilId, fuarId, deneme]);

  const gruplar = useMemo(() => pencereGruplari(metinler), [metinler]);
  // Personelin kendi /current bayrakları anlamsızdır (önizlemede hiçbir şey onaylanmış görünmez).
  const onaylandiMi = (g) => !personel && g.metinler.every((m) => m.onaylandi);

  // Pencerenin seçilen metinleri için POST /accept; hepsi yazılınca liste yeniden okunur (bayraklar sunucudan), pencere kapanır. Hata
  // pencerededir. Onay yazıldıktan sonra yeniden okuma düşerse bayraklar yerel olarak işaretlenir: yazılan onay kaybolmasın.
  const onayla = async (secilen) => {
    for (const m of secilen) await onayKaydet(m.anahtar, { languageId: m.languageId, fuarId });
    try {
      setMetinler(await getGuncelMetinler({ languageId: dilId, fuarId }));
    } catch {
      setMetinler((onceki) => (onceki || []).map((m) => (secilen.some((s) => s.anahtar === m.anahtar) ? { ...m, onaylandi: true } : m)));
    }
    setAcikPencere(null);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Card>
          <CardHeader className="d-flex align-items-center gap-2 flex-wrap">
            <i className="bx bx-book-content fs-18 text-muted" />
            <h5 className="card-title mb-0 flex-grow-1">{t("fuarKurallari.fuarKurallari")}</h5>
          </CardHeader>
          <CardBody>
            {personel && <Alert color="info" className="small mb-3">{t("fuarKurallari.onizlemeNotu")}</Alert>}
            {metinler === null ? (
              <div className="text-center py-4" data-testid="metinler-yukleniyor"><Spinner color="primary" /></div>
            ) : (
              <>
                {hata && (
                  <Alert color="danger" className="small d-flex flex-wrap align-items-center gap-2" data-testid="metinler-hata">
                    <span className="flex-grow-1">{t("onayKapisi.yuklenemedi")}: {hata}</span>
                    <Button color="light" size="sm" onClick={() => setDeneme((n) => n + 1)}>{t("onayKapisi.tekrarDene")}</Button>
                  </Alert>
                )}
                <div className="fuar-kurallari-liste">
                  {gruplar.map((g) => (
                    <KuralSatiri key={g.ad} ikon={PENCERELER[g.ad].ikon} ad={t(PENCERELER[g.ad].baslikAnahtari)} onaylandi={onaylandiMi(g)}
                      ac={() => setAcikPencere(g.ad)} testId={`satir-${g.ad}`} />
                  ))}
                  {!hata && gruplar.length === 0 && <p className="text-muted mb-0">{t("fuarKurallari.metinYok")}</p>}
                </div>
              </>
            )}
          </CardBody>
        </Card>

        {gruplar.map((g) => (
          <OnayPenceresi key={g.ad} acik={acikPencere === g.ad} kapat={() => setAcikPencere(null)} baslik={t(PENCERELER[g.ad].baslikAnahtari)}
            coklu={g.ad === "kurallar"} dilId={dilId} salt={personel} metinler={g.metinler} onaylandi={onaylandiMi(g)} onayla={onayla} />
        ))}
      </Container>
    </div>
  );
}
